/*
This file is located in the main branch
Last time the file was updated (UTC): [19.08.2024 14:24:30]
Permissions to update outside the main branch: [true]
*/

import { AxiosRequestConfig } from 'axios';
import { refresh } from 'axios/tokenApi';
import { Auth } from 'models/auth';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios, { axiosPaylaterApi } from '../axios/axios.paylater-api';
import useAuth, { initialAuth } from './useAuth';

let refreshPromise: Promise<Auth> | undefined = undefined;

export const getRefreshedAuth = async (config: AxiosRequestConfig): Promise<Auth> => {
    const resp = await refresh(axios, config);
    const userInfo = resp.data || initialAuth;

    return Promise.resolve({
        user: userInfo.userName,
        userFullName: `${userInfo.lastName} ${userInfo.firstName} ${userInfo.middleName}`,
        userId: userInfo.userId,
        roles: userInfo.roles,
        apiCrmToken: userInfo.apiCrmToken,
        accessTokenExpires: userInfo.tokenExpires,
        accessTokenValid: true,
        tariffEditingAccess: userInfo.tariffEditingAccess,
        tariffEditableBillServices: userInfo.tariffEditableBillServices,
        canViewOrdersWithActiveProlongationAgreements:
            userInfo.canViewOrdersWithActiveProlongationAgreements,
        portalUserEmployerId: userInfo.portalUserEmployerId,
        showMerchantInstruction: userInfo.showMerchantInstruction,
        userStartPage: userInfo.userStartPage,
    });
};

const useApi = () => {
    const { auth, setAuth } = useAuth();
    const { i18n } = useTranslation();

    useEffect(() => {
        const requestIntercept = axiosPaylaterApi.interceptors.request.use(
            async (config) => {
                if (config.headers && !config.headers['Accept-Language']) {
                    config.headers['Accept-Language'] = i18n.language;
                }

                if (auth?.accessTokenValid === false && auth?.accessTokenExpires) {
                    try {
                        if (!refreshPromise) {
                            refreshPromise = getRefreshedAuth(config);
                        }

                        const newAuth = await refreshPromise;
                        setAuth(newAuth);
                    } catch (error) {
                        setAuth(initialAuth);
                    } finally {
                        refreshPromise = undefined;
                    }
                } else {
                    return config;
                }
            },
            (error) => Promise.reject(error)
        );

        const responseIntercept = axiosPaylaterApi.interceptors.response.use(
            (response) => response,
            async (error) => {
                const prevRequest = error?.config;
                if (error?.response?.status === 401 && !prevRequest?.sent) {
                    prevRequest.sent = true;
                    try {
                        if (!refreshPromise) {
                            refreshPromise = getRefreshedAuth(prevRequest);
                        }

                        const newAuth = await refreshPromise;
                        setAuth(newAuth);

                        return axiosPaylaterApi(prevRequest);
                    } catch (error) {
                        setAuth(initialAuth);
                    } finally {
                        refreshPromise = undefined;
                    }
                }
                return Promise.reject(error);
            }
        );

        return () => {
            axiosPaylaterApi.interceptors.request.eject(requestIntercept);
            axiosPaylaterApi.interceptors.response.eject(responseIntercept);
        };
    }, [auth]);

    return axiosPaylaterApi;
};

export default useApi;
